import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@dpem/design-system';

import { presentError } from '@/lib/api/errors';
import { SurfaceContractError } from '@/router/surfaces';
import { ErrorPage } from './ErrorPage';

/**
 * The last line: a render-time throw anywhere below here becomes a page instead
 * of a white screen.
 *
 * React has no hook form of this — `componentDidCatch` is the only way to catch a
 * render error — so this is the one class component in the app.
 *
 * It is placed *inside* the shell, around the surface outlet, not around the whole
 * tree. A surface that throws should leave the header, rail and navigation intact
 * so the user can go somewhere else; a boundary at the root would take the whole
 * workspace down with it and the only recovery would be a reload.
 *
 * A thrown error's own `message` is not shown. For a known API failure
 * `presentError` has text written for a person; for anything else the message is a
 * programmer's sentence about a null reference, which tells a practitioner nothing
 * and a support engineer less than the console entry already does.
 */

export interface AppErrorBoundaryProps {
  children: ReactNode;
  /**
   * Changing this remounts the boundary, clearing a caught error. The router
   * passes the pathname, so navigating away from a broken surface recovers
   * without a reload.
   */
  resetKey?: string;
}

interface AppErrorBoundaryState {
  error: Error | null;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  override state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  override componentDidUpdate(previous: AppErrorBoundaryProps): void {
    if (previous.resetKey !== this.props.resetKey && this.state.error !== null) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is the only sink a frontend has for an unexpected throw, and
    // without the component stack these are close to unfixable. Deliberately not
    // shipped anywhere: a render error's payload can contain response content.
    console.error('Unhandled render error', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    if (error instanceof SurfaceContractError) {
      return (
        <ErrorPage
          code="Surface unavailable"
          title="This part of the workspace failed to load"
          detail="The workspace could not start this surface. Reloading usually fixes it; if it does not, the deployment is incomplete."
        >
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </ErrorPage>
      );
    }

    const presented = presentError(error);

    return (
      <ErrorPage
        code={presented.code}
        title={presented.title}
        detail={
          presented.code === 'UNKNOWN'
            ? 'An unexpected error interrupted this view. Nothing was saved.'
            : (presented.detail ?? 'Nothing was saved.')
        }
        traceId={presented.traceId}
      >
        <Button onClick={() => this.setState({ error: null })}>Try again</Button>
      </ErrorPage>
    );
  }
}
